import { supabase } from './supabaseClient';
import { AuditService } from './auditService';

export interface CreateDriverInvoiceParams {
  driverId: string;
  ticketIds?: string[]; // For ticket charges
  billingType: 'immediate' | 'monthly';
  periodStart?: string; // For monthly (YYYY-MM-DD)
  periodEnd?: string; // For monthly (YYYY-MM-DD)
  organisationId?: string; // Optional, will be fetched from context if not provided
}

export interface DriverInvoiceItem {
  itemType: 'ticket_charge' | 'admin_fee' | 'late_fee' | 'discount';
  description: string;
  ticketId?: string;
  hireId?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  metadata?: Record<string, any>;
}

export interface DriverInvoice {
  id: string;
  invoice_number: string;
  organisation_id: string;
  driver_id: string;
  invoice_date: string;
  due_date: string;
  period_start?: string;
  period_end?: string;
  billing_type: 'immediate' | 'monthly';
  ticket_charges_total: number;
  admin_fees_total: number;
  late_fees: number;
  discount_amount: number;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'disputed' | 'cancelled';
  sent_at?: string;
  viewed_at?: string;
  paid_at?: string;
  stripe_payment_intent_id?: string;
  stripe_charge_id?: string;
  payment_method?: string;
  reminder_count: number;
  last_reminder_sent_at?: string;
  dispute_reason?: string;
  dispute_submitted_at?: string;
  dispute_resolved_at?: string;
  dispute_resolution?: string;
  pdf_url?: string;
  stripe_invoice_url?: string;
  platform_fee_percentage?: number;
  platform_fee_amount?: number;
  created_at: string;
  updated_at: string;
}

export interface SavedPaymentMethod {
  id: string;
  driver_id: string;
  organisation_id: string;
  stripe_payment_method_id: string;
  stripe_customer_id?: string;
  card_brand?: string;
  card_last4: string;
  card_exp_month: number;
  card_exp_year: number;
  payment_method_type: string;
  is_default: boolean;
  is_active: boolean;
  failed_payment_count: number;
  billing_address?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export const DriverInvoiceService = {
  /**
   * Create invoice for driver
   */
  async createInvoice(params: CreateDriverInvoiceParams): Promise<{ success: boolean; invoiceId?: string; error?: string }> {
    try {
      const { driverId, ticketIds, billingType, periodStart, periodEnd, organisationId } = params;

      // Get organization ID from current user if not provided
      let orgId = organisationId;
      if (!orgId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        const { data: profile } = await supabase
          .from('profiles')
          .select('organisation_id')
          .eq('id', user.id)
          .single();

        if (!profile) throw new Error('User not associated with any organization');
        orgId = profile.organisation_id;
      }

      // Get billing settings
      const { data: billingSettings, error: billingError } = await supabase
        .from('organisation_billing_settings')
        .select('*')
        .eq('organisation_id', orgId)
        .single();

      if (billingError || !billingSettings) {
        throw new Error('Billing settings not configured for organization');
      }

      // Generate invoice number
      const { data: invoiceNumber, error: invoiceNumberError } = await supabase
        .rpc('generate_driver_invoice_number', { org_uuid: orgId });

      if (invoiceNumberError) throw invoiceNumberError;

      // Calculate due date
      const invoiceDate = new Date();
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + (billingSettings.payment_terms_days || 7));

      // Create invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('driver_invoices')
        .insert({
          invoice_number: invoiceNumber,
          organisation_id: orgId,
          driver_id: driverId,
          invoice_date: invoiceDate.toISOString().split('T')[0],
          due_date: dueDate.toISOString().split('T')[0],
          period_start: periodStart,
          period_end: periodEnd,
          billing_type: billingType,
          total_amount: 0,
          amount_due: 0,
          status: 'draft'
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // If ticket IDs provided, add line items
      if (ticketIds && ticketIds.length > 0) {
        await this.addTicketCharges(invoice.id, ticketIds);
      }

      // Audit log: Invoice created
      await AuditService.log({
        action: 'invoice_created',
        category: 'invoice',
        description: `Created invoice ${invoice.invoice_number}`,
        resource_type: 'driver_invoice',
        resource_id: invoice.id,
        metadata: { invoice_number: invoice.invoice_number, driver_id: driverId }
      }).catch((err: unknown) => console.warn('Failed to log invoice creation:', err));

      return { success: true, invoiceId: invoice.id };
    } catch (error: any) {
      console.error('Error creating driver invoice:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Add ticket charges to existing invoice
   */
  async addTicketCharges(invoiceId: string, ticketIds: string[]): Promise<{ success: boolean; error?: string }> {
    try {
      // Get invoice details for organisation_id
      const { data: invoice, error: invoiceError } = await supabase
        .from('driver_invoices')
        .select('organisation_id')
        .eq('id', invoiceId)
        .single();

      if (invoiceError) throw invoiceError;

      // Use transaction-safe PostgreSQL function to add charges atomically
      // This prevents race conditions, double-charging, and stale balances
      const { data: result, error: rpcError } = await supabase.rpc('add_ticket_charges_to_invoice', {
        p_invoice_id: invoiceId,
        p_ticket_ids: ticketIds,
        p_organisation_id: invoice.organisation_id
      });

      if (rpcError) throw rpcError;

      // Check if the function returned an error
      if (result && !result.success) {
        throw new Error(result.error || 'Failed to add ticket charges');
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error adding ticket charges:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Add admin fee to invoice
   */
  async addAdminFee(invoiceId: string, ticketId: string, hireId?: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Get invoice and billing settings
      const { data: invoice } = await supabase
        .from('driver_invoices')
        .select('organisation_id')
        .eq('id', invoiceId)
        .single();

      if (!invoice) throw new Error('Invoice not found');

      const { data: settings } = await supabase
        .from('organisation_billing_settings')
        .select('*')
        .eq('organisation_id', invoice.organisation_id)
        .single();

      if (!settings) throw new Error('Billing settings not found');

      // Get ticket to calculate admin fee
      const { data: ticket } = await supabase
        .from('tickets')
        .select('*')
        .eq('id', ticketId)
        .single();

      if (!ticket) throw new Error('Ticket not found');

      // Calculate admin fee
      let adminFee = 0;
      if (settings.admin_fee_type === 'fixed') {
        adminFee = settings.admin_fee_fixed_amount || 0;
      } else if (settings.admin_fee_type === 'percentage') {
        adminFee = (ticket.amount * (settings.admin_fee_percentage || 0)) / 100;
      } else if (settings.admin_fee_type === 'tiered' && settings.admin_fee_tiers) {
        const tiers = settings.admin_fee_tiers as Array<{ min: number; max: number; fee: number }>;
        const tier = tiers.find(t => ticket.amount >= t.min && ticket.amount <= t.max);
        adminFee = tier ? tier.fee : 0;
      }

      // Add line item
      const { error } = await supabase
        .from('driver_invoice_items')
        .insert({
          invoice_id: invoiceId,
          organisation_id: invoice.organisation_id,
          item_type: 'admin_fee',
          description: `Admin fee for PCN ${ticket.pcn_number}`,
          ticket_id: ticketId,
          hire_id: hireId,
          quantity: 1,
          unit_price: adminFee,
          total_price: adminFee,
          metadata: {
            pcn_number: ticket.pcn_number,
            original_ticket_amount: ticket.amount
          }
        });

      if (error) throw error;

      // Update invoice totals
      await this.recalculateInvoiceTotals(invoiceId);

      // Update ticket admin fee charged
      await supabase
        .from('tickets')
        .update({ admin_fee_charged: adminFee })
        .eq('id', ticketId);

      return { success: true };
    } catch (error: any) {
      console.error('Error adding admin fee:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Recalculate invoice totals from line items
   */
  async recalculateInvoiceTotals(invoiceId: string): Promise<void> {
    const { data: items } = await supabase
      .from('driver_invoice_items')
      .select('item_type, total_price')
      .eq('invoice_id', invoiceId);

    if (!items) return;

    const ticket_charges_total = items
      .filter(i => i.item_type === 'ticket_charge')
      .reduce((sum, i) => sum + parseFloat(i.total_price.toString()), 0);

    const admin_fees_total = items
      .filter(i => i.item_type === 'admin_fee')
      .reduce((sum, i) => sum + parseFloat(i.total_price.toString()), 0);

    const late_fees = items
      .filter(i => i.item_type === 'late_fee')
      .reduce((sum, i) => sum + parseFloat(i.total_price.toString()), 0);

    const discount_amount = items
      .filter(i => i.item_type === 'discount')
      .reduce((sum, i) => sum + parseFloat(i.total_price.toString()), 0);

    const total_amount = ticket_charges_total + admin_fees_total + late_fees - discount_amount;

    await supabase
      .from('driver_invoices')
      .update({
        ticket_charges_total,
        admin_fees_total,
        late_fees,
        discount_amount,
        total_amount,
        amount_due: total_amount // Initially amount_due = total_amount
      })
      .eq('id', invoiceId);
  },

  /**
   * Get driver invoices with filters
   */
  async getDriverInvoices(driverId: string, filters?: {
    status?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ success: boolean; data?: DriverInvoice[]; error?: string }> {
    try {
      let query = supabase
        .from('driver_invoices')
        .select('*')
        .eq('driver_id', driverId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.dateFrom) {
        query = query.gte('invoice_date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('invoice_date', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data as DriverInvoice[] };
    } catch (error: any) {
      console.error('Error getting driver invoices:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get organization driver invoices
   */
  async getOrgDriverInvoices(organisationId: string, filters?: {
    status?: string;
    driverId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ success: boolean; data?: DriverInvoice[]; error?: string }> {
    try {
      let query = supabase
        .from('driver_invoices')
        .select('*')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.driverId) {
        query = query.eq('driver_id', filters.driverId);
      }

      if (filters?.dateFrom) {
        query = query.gte('invoice_date', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('invoice_date', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data as DriverInvoice[] };
    } catch (error: any) {
      console.error('Error getting org driver invoices:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Send invoice to driver
   */
  async sendInvoice(invoiceId: string, paymentMethod: 'stripe_invoice' | 'checkout' | 'saved' | 'manual' = 'stripe_invoice'): Promise<{ success: boolean; invoiceUrl?: string; invoicePdf?: string; error?: string }> {
    try {
      // Get invoice with driver details and organization Stripe Connect status
      const { data: invoice, error: invoiceError } = await supabase
        .from('driver_invoices')
        .select(`
          *,
          drivers:driver_id (id, name, email),
          organisations:organisation_id (
            id,
            name,
            email,
            phone,
            stripe_connect_account_id,
            stripe_connect_status,
            stripe_connect_charges_enabled,
            stripe_connect_payouts_enabled
          )
        `)
        .eq('id', invoiceId)
        .single();

      if (invoiceError || !invoice) {
        throw new Error('Invoice not found');
      }

      const driverRecord = Array.isArray(invoice.drivers) ? invoice.drivers[0] : invoice.drivers;
      const organisationRecord = Array.isArray(invoice.organisations) ? invoice.organisations[0] : invoice.organisations;
      const organisationName = organisationRecord?.name || 'PCN Guard';
      const organisationEmail = organisationRecord?.email || undefined;
      const organisationPhone = organisationRecord?.phone || undefined;

      if (!driverRecord?.email) {
        throw new Error('Driver has no email address');
      }

      // Check Stripe Connect status for new Stripe Invoice method
      if (paymentMethod === 'stripe_invoice') {
        if (!organisationRecord?.stripe_connect_account_id) {
          throw new Error('Stripe account not connected. Please connect your Stripe account in Settings > Organization > Payment Processing before creating driver invoices.');
        }

        if (organisationRecord?.stripe_connect_status !== 'active') {
          throw new Error(`Stripe account is ${organisationRecord?.stripe_connect_status}. Please complete onboarding in Settings > Organization > Payment Processing.`);
        }

        if (!organisationRecord?.stripe_connect_charges_enabled) {
          throw new Error('Stripe account cannot accept charges. Please check your Stripe dashboard or contact Stripe support.');
        }
      }

      // Get line items
      const { data: items } = await supabase
        .from('driver_invoice_items')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: true });

      const emailSubject = `Invoice ${invoice.invoice_number} from ${organisationName}`;

      // Handle new Stripe Invoice method (Stripe Connect)
      if (paymentMethod === 'stripe_invoice') {
        const { data, error } = await supabase.functions.invoke('create-stripe-invoice', {
          body: { invoiceId: invoice.id },
        });

        if (error) throw error;
        if (!data.success) throw new Error(data.error || 'Failed to create Stripe invoice');

        // Invoice created successfully - status updated to 'sent' by edge function
        // Return invoice URLs for driver to pay
        return {
          success: true,
          invoiceUrl: data.invoiceUrl,
          invoicePdf: data.invoicePdf,
        };
      }

      // Generate payment link if using Stripe Checkout (legacy method)
      let paymentLink: string | undefined;
      if (paymentMethod === 'checkout') {
        const { data: result, error: invokeError } = await supabase.functions.invoke('create-invoice-payment', {
          body: {
            invoiceId: invoice.id,
            paymentMethod: 'checkout',
          },
        });

        if (invokeError) throw invokeError;
        if (result.success) {
          paymentLink = result.paymentUrl;
        }
      } else if (paymentMethod === 'saved') {
        // Charge immediately with saved payment method
        const { data: result, error: invokeError } = await supabase.functions.invoke('create-invoice-payment', {
          body: {
            invoiceId: invoice.id,
            paymentMethod: 'saved',
          },
        });

        if (invokeError) throw invokeError;
        if (!result.success) {
          throw new Error(result.error || 'Failed to charge saved payment method');
        }

        // Payment initiated successfully - webhook will update status to 'paid'
        // Send confirmation email to driver
        await supabase.functions.invoke('send-email', {
          body: {
            to: driverRecord.email,
            subject: emailSubject,
            template: 'DriverInvoiceEmail',
            data: {
              invoiceNumber: invoice.invoice_number,
              driverName: driverRecord.name,
              invoiceDate: new Date(invoice.invoice_date).toLocaleDateString('en-GB'),
              dueDate: new Date(invoice.due_date).toLocaleDateString('en-GB'),
              lineItems: (items || []).map(item => ({
                description: item.description,
                amount: parseFloat(item.total_price.toString()),
              })),
              ticketChargesTotal: parseFloat(invoice.ticket_charges_total.toString()),
              adminFeesTotal: parseFloat(invoice.admin_fees_total.toString()),
              totalAmount: parseFloat(invoice.total_amount.toString()),
              paymentLink: undefined, // No link needed - already charged
              organisationName,
              organisationEmail,
              organisationPhone,
            },
          },
        });

        // Return success - webhook will update status asynchronously
        return { success: true };
      }

      // Prepare email data
      const emailData = {
        invoiceNumber: invoice.invoice_number,
        driverName: driverRecord.name,
        invoiceDate: new Date(invoice.invoice_date).toLocaleDateString('en-GB'),
        dueDate: new Date(invoice.due_date).toLocaleDateString('en-GB'),
        lineItems: (items || []).map(item => ({
          description: item.description,
          amount: parseFloat(item.total_price.toString()),
        })),
        ticketChargesTotal: parseFloat(invoice.ticket_charges_total.toString()),
        adminFeesTotal: parseFloat(invoice.admin_fees_total.toString()),
        totalAmount: parseFloat(invoice.total_amount.toString()),
        paymentLink,
        organisationName,
        organisationEmail,
        organisationPhone,
      };

      // Send email
      const { error: emailError } = await supabase.functions.invoke('send-email', {
        body: {
          to: driverRecord.email,
          subject: emailSubject,
          template: 'DriverInvoiceEmail',
          data: emailData,
        },
      });

      if (emailError) throw emailError;

      // Update invoice status
      await supabase
        .from('driver_invoices')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      return { success: true };
    } catch (error: any) {
      console.error('Error sending invoice:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Mark invoice as paid
   */
  async markInvoicePaid(invoiceId: string, paymentMethod: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Calculate total first
      const { data: total, error: rpcError } = await supabase.rpc('calculate_invoice_total', {
        invoice_uuid: invoiceId
      });

      if (rpcError) throw rpcError;

      // Then update invoice
      await supabase
        .from('driver_invoices')
        .update({
          status: 'paid',
          paid_at: new Date().toISOString(),
          payment_method: paymentMethod,
          amount_paid: total,
          amount_due: 0
        })
        .eq('id', invoiceId);

      return { success: true };
    } catch (error: any) {
      console.error('Error marking invoice paid:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Submit dispute for invoice
   */
  async submitDispute(invoiceId: string, reason: string): Promise<{ success: boolean; error?: string }> {
    try {
      await supabase
        .from('driver_invoices')
        .update({
          status: 'disputed',
          dispute_reason: reason,
          dispute_submitted_at: new Date().toISOString()
        })
        .eq('id', invoiceId);

      // TODO: Send notification to organization

      return { success: true };
    } catch (error: any) {
      console.error('Error submitting dispute:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if organization can create Stripe invoices
   * (i.e., has Stripe Connect account connected and active)
   */
  async canCreateStripeInvoices(organisationId: string): Promise<{ canCreate: boolean; reason?: string }> {
    try {
      const { data: org, error } = await supabase
        .from('organisations')
        .select('stripe_connect_account_id, stripe_connect_status, stripe_connect_charges_enabled')
        .eq('id', organisationId)
        .single();

      if (error) throw error;
      if (!org) throw new Error('Organisation not found');

      if (!org.stripe_connect_account_id) {
        return {
          canCreate: false,
          reason: 'Stripe Connect is not started. You can still use Manual Payment and complete Stripe setup later in Settings > Driver Billing.',
        };
      }

      if (org.stripe_connect_status !== 'active') {
        return {
          canCreate: false,
          reason: `Stripe Connect is ${org.stripe_connect_status}. Finish onboarding in Settings > Driver Billing to enable Stripe Hosted Invoice.`,
        };
      }

      if (!org.stripe_connect_charges_enabled) {
        return {
          canCreate: false,
          reason: 'Stripe Connect cannot accept charges yet. You can continue with Manual Payment while Stripe setup is completed.',
        };
      }

      return { canCreate: true };
    } catch (error: any) {
      console.error('Error checking Stripe invoice capability:', error);
      return {
        canCreate: false,
        reason: error.message || 'Failed to check Stripe connection status',
      };
    }
  }
};

/**
 * Driver Invoice Status Type
 * Used by FleetMind recommendations to consider invoice status
 */
export interface DriverInvoiceStatus {
  hasInvoice: boolean;
  status: 'draft' | 'sent' | 'viewed' | 'paid' | 'overdue' | 'disputed' | 'cancelled' | null;
  invoiceId: string | null;
  amount: number | null;
  dueDate: string | null;
  paidAt: string | null;
}

/**
 * Get driver invoice status for a specific ticket
 *
 * This function is used by FleetMind recommendations to check if a ticket
 * has been invoiced to the driver and what the payment status is.
 *
 * @param ticketId - The ticket ID to check
 * @returns Promise<DriverInvoiceStatus> - Invoice status or null if no invoice
 */
export async function getDriverInvoiceStatusForTicket(ticketId: string): Promise<DriverInvoiceStatus> {
  try {
    const { data: items } = await supabase
      .from('driver_invoice_items')
      .select(`
        invoice_id,
        total_price,
        driver_invoices!inner (
          id,
          status,
          total_amount,
          due_date,
          paid_at
        )
      `)
      .eq('ticket_id', ticketId)
      .eq('item_type', 'ticket_charge')
      .order('created_at', { ascending: false })
      .limit(1);

    if (!items || items.length === 0) {
      return {
        hasInvoice: false,
        status: null,
        invoiceId: null,
        amount: null,
        dueDate: null,
        paidAt: null
      };
    }

    const invoice = items[0].driver_invoices as any;

    return {
      hasInvoice: true,
      status: invoice.status,
      invoiceId: invoice.id,
      amount: invoice.total_amount,
      dueDate: invoice.due_date,
      paidAt: invoice.paid_at
    };
  } catch (error) {
    console.error('Error fetching driver invoice status:', error);
    return {
      hasInvoice: false,
      status: null,
      invoiceId: null,
      amount: null,
      dueDate: null,
      paidAt: null
    };
  }
}
